import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Badge, Box, Button, Popover, Stack, Typography } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useTranslate } from 'react-admin';

interface FiltersPopoverProps {
  /** Number of currently active filters, shown as a badge on the button. */
  activeCount?: number;
  children: ReactNode;
  onClear?: () => void;
}

/**
 * The one filter affordance (foundations.md §7.14): a single Filters
 * button opens a popover holding every filter. Never a persistent or
 * collapsible filter panel. Use `PillTabs`/an inline search field directly
 * in the header instead of this component when search is the only filter.
 */
export function FiltersPopover({ activeCount = 0, children, onClear }: FiltersPopoverProps) {
  const translate = useTranslate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const hasActiveFilters = activeCount > 0;

  return (
    <>
      <Badge
        color="primary"
        badgeContent={activeCount}
        invisible={!hasActiveFilters}
        sx={{ '& .MuiBadge-badge': { right: 6, top: 4 } }}
      >
        <Button
          variant={open || hasActiveFilters ? 'contained' : 'outlined'}
          startIcon={<TuneOutlinedIcon fontSize="small" />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          {translate('pay.sectionHeader.filters')}
        </Button>
      </Badge>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 'min(660px, 92vw)',
              borderRadius: 1.5,
              p: 2,
              boxShadow: (theme) => theme.custom.popoverShadow,
            },
          },
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {translate('pay.filtersPopover.title')}
          </Typography>
          <Box>{children}</Box>
          {onClear && (
            <Stack direction="row" justifyContent="flex-end">
              <Button size="small" onClick={onClear}>
                {translate('pay.filtersPopover.clear')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Popover>
    </>
  );
}
